import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Calendar, 
  CheckCircle2, 
  FileText,
  Link2,
  Send,
  Twitter,
  Music2,
  ArrowRight,
  Clock,
  TrendingUp,
  AlertCircle,
  BarChart3
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
    fetchAnalytics();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setStats(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/simple`);
      setAnalytics(response.data);
    } catch (error) {
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'telegram': return <Send className="w-4 h-4" />;
      case 'twitter': return <Twitter className="w-4 h-4" />;
      case 'tiktok': return <Music2 className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform.toLowerCase()) {
      case 'telegram': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'twitter': return 'bg-sky-500/10 text-sky-400 border-sky-500/20';
      case 'tiktok': return 'bg-pink-500/10 text-pink-400 border-pink-500/20';
      default: return 'bg-primary/10 text-primary border-primary/20';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-accent/10 text-accent border-accent/20">Published</Badge>;
      case 'scheduled':
        return <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">Scheduled</Badge>;
      case 'draft':
        return <Badge className="bg-muted text-muted-foreground border-border">Draft</Badge>;
      case 'failed':
        return <Badge className="bg-destructive/10 text-destructive border-destructive/20">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back! Here's your content overview.</p>
        </div>
        <Link to="/create">
          <Button className="glow-primary" data-testid="create-post-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Post
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border-border/50 hover:border-primary/30 transition-colors">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Posts</CardTitle>
            <FileText className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono" data-testid="total-posts-count">
              {stats?.total_posts || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border/50 hover:border-yellow-500/30 transition-colors">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Scheduled</CardTitle>
            <Calendar className="w-4 h-4 text-yellow-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-yellow-400" data-testid="scheduled-posts-count">
              {stats?.scheduled_posts || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border/50 hover:border-accent/30 transition-colors">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Published</CardTitle>
            <CheckCircle2 className="w-4 h-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-accent" data-testid="published-posts-count">
              {stats?.published_posts || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border/50 hover:border-primary/30 transition-colors">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Connected</CardTitle>
            <Link2 className="w-4 h-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-primary" data-testid="connected-accounts-count">
              {stats?.connected_accounts || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Analytics */}
      {analytics && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-green-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-400 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Posts Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-green-400">{analytics.posts_today}</div>
              <p className="text-xs text-gray-400 mt-1">
                {analytics.posts_today_failed > 0 && (
                  <span className="text-red-400">{analytics.posts_today_failed} failed</span>
                )}
                {analytics.posts_today_failed === 0 && 'All successful'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border-blue-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-400 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Posts This Week
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-blue-400">{analytics.posts_this_week}</div>
              <p className="text-xs text-gray-400 mt-1">
                {analytics.posts_this_week_failed > 0 && (
                  <span className="text-red-400">{analytics.posts_this_week_failed} failed</span>
                )}
                {analytics.posts_this_week_failed === 0 && 'All successful'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/5 border-purple-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-purple-400 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Success Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-purple-400">{analytics.success_rate_week}%</div>
              <p className="text-xs text-gray-400 mt-1">Last 7 days</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Channels Today */}
      {analytics?.by_channel_today?.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg">Top Channels Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.by_channel_today.slice(0, 5).map((item, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-gray-800/50 rounded">
                  <span className="text-sm">{item.channel || 'Unknown'}</span>
                  <Badge variant="outline">{item.count} posts</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Posts */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Recent Posts</h2>
          <Link to="/posts" className="text-sm text-primary hover:underline flex items-center gap-1">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {stats?.recent_posts?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_posts.map((post) => (
              <Card 
                key={post.id} 
                className="bg-card border-border/50 hover:border-primary/30 transition-colors"
                data-testid={`post-card-${post.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm line-clamp-2 mb-2">{post.content}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {post.platforms.map((platform) => (
                          <span 
                            key={platform}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${getPlatformColor(platform)}`}
                          >
                            {getPlatformIcon(platform)}
                            {platform}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(post.status)}
                      <span className="text-xs text-muted-foreground font-mono flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {format(new Date(post.created_at), 'MMM d, HH:mm')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-border/50">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No posts yet</h3>
              <p className="text-muted-foreground mb-4">Create your first post to get started</p>
              <Link to="/create">
                <Button className="glow-primary" data-testid="empty-create-post-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Post
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Actions */}
      {stats?.connected_accounts === 0 && (
        <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold mb-1">Connect Your Accounts</h3>
                <p className="text-muted-foreground text-sm">
                  Link your social media accounts to start publishing content.
                </p>
              </div>
              <Link to="/accounts">
                <Button variant="outline" className="border-primary/50 hover:bg-primary/10" data-testid="connect-accounts-btn">
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect Accounts
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
